import { Controller, useFieldArray, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Button, Input, Select, Typography } from 'antd';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import {
  ECO_CERTIFICATION_TYPES,
  MAX_ECO_CERTIFICATIONS,
  type ProfileFormValues,
} from '../model/schema';

const { Text } = Typography;

/**
 * Structured eco credentials — standard, issuer, number, expiry.
 *
 * <p>Separate from the eco certificate uploads next to it, and on purpose: a
 * scan proves a claim but cannot be searched. "Organically certified, valid
 * until 2027" is what a buyer screening suppliers on ESG criteria filters by,
 * and what a European tender asks for — neither can be answered by a filename.
 *
 * Only rendered for an eco producer. The backend refuses these rows outright
 * for anyone else, so showing the block to an ordinary producer would only
 * offer them a form that cannot be saved.
 */
export function EcoCertifications() {
  const { t } = useTranslation();
  const form = useFormContext<ProfileFormValues>();
  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: 'ecoCertifications',
  });
  const errors = form.formState.errors.ecoCertifications;

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-col gap-1">
        <Text strong>{t('profile.eco.title')}</Text>
        <Text type="secondary" style={{ fontSize: 12 }}>
          {t('profile.eco.hint')}
        </Text>
      </div>

      {fields.length === 0 ? (
        <Text type="secondary" style={{ fontSize: 13 }}>
          {t('profile.eco.empty')}
        </Text>
      ) : null}

      {fields.map((row, i) => (
        <div
          key={row.id}
          className="grid gap-3 md:grid-cols-2"
          style={{
            padding: 16,
            borderRadius: 12,
            border: '1px solid var(--ant-color-border-secondary)',
          }}
        >
          <Controller
            control={form.control}
            name={`ecoCertifications.${i}.type`}
            render={({ field }) => (
              <label className="flex flex-col gap-1">
                <Text style={{ fontSize: 12 }}>{t('profile.eco.type')}</Text>
                <Select
                  value={field.value}
                  onChange={field.onChange}
                  options={ECO_CERTIFICATION_TYPES.map((v) => ({
                    value: v,
                    label: t(`profile.eco.types.${v}`),
                  }))}
                />
              </label>
            )}
          />

          <label className="flex flex-col gap-1">
            <Text style={{ fontSize: 12 }}>{t('profile.eco.name')}</Text>
            <Input
              placeholder="ISO 14001"
              status={errors?.[i]?.name ? 'error' : undefined}
              {...form.register(`ecoCertifications.${i}.name`)}
            />
            {errors?.[i]?.name ? (
              <Text type="danger" style={{ fontSize: 12 }} role="alert">
                {errors[i]?.name?.message}
              </Text>
            ) : null}
          </label>

          <label className="flex flex-col gap-1">
            <Text style={{ fontSize: 12 }}>{t('profile.eco.issuer')}</Text>
            <Input
              placeholder="Ecocert"
              {...form.register(`ecoCertifications.${i}.issuer`)}
            />
          </label>

          <label className="flex flex-col gap-1">
            <Text style={{ fontSize: 12 }}>{t('profile.eco.number')}</Text>
            <Input {...form.register(`ecoCertifications.${i}.number`)} />
          </label>

          <label className="flex flex-col gap-1">
            <Text style={{ fontSize: 12 }}>{t('profile.eco.validUntil')}</Text>
            {/* A native date input rather than antd's picker: the value is a
                plain ISO day — an expiry printed on a certificate, where a
                timezone would only move it — and the picker would pull dayjs
                in as a direct dependency for no gain. */}
            <Input type="date" {...form.register(`ecoCertifications.${i}.validUntil`)} />
          </label>

          <div className="flex items-end">
            <Button
              danger
              type="text"
              icon={<DeleteOutlined />}
              onClick={() => remove(i)}
            >
              {t('common.remove')}
            </Button>
          </div>
        </div>
      ))}

      <div>
        <Button
          icon={<PlusOutlined />}
          disabled={fields.length >= MAX_ECO_CERTIFICATIONS}
          onClick={() =>
            append({
              type: 'STANDARD',
              name: '',
              issuer: '',
              number: '',
              validUntil: '',
              mediaId: null,
            })
          }
        >
          {t('profile.eco.add')}
        </Button>
      </div>
    </div>
  );
}
